import React, { useState, useRef } from 'react';
import { getWrestlerName, getWrestlerNameUpper, wrestlerMatchesSearch } from './wrestlerUtils';
import { MatchMaker, hasSynergyWithGroup, isMatchSpecialist, SlotPreview } from './BookingPage';
import UnifiedWrestlerCard, { WrestlerCardStyles } from './WrestlerCard';
import { PLES, getPLELogo } from './plesDatabase';
import { getPLETheme, getPLEBg } from './pleThemes';
import { MATCH_TYPES, MATCH_RULES, getMatchPrice } from './matchTypesDatabase';
import { PROMO_ACTIONS } from './promoDatabase';

// ── Constants ─────────────────────────────────────────────────────────

const BRAND_LOGOS = {
  'Raw':        process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
  'SmackDown':  process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
  'NXT':        process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
  'NXT 2.0':    process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
  'WCW':        process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
  'ECW':        process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
  'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
};

// Vertical background images per brand (258x512px portrait)
const BRAND_BG_IMAGES = {
  'Raw':        process.env.PUBLIC_URL + '/brands/backgrounds/RAW_BG.png',
  'SmackDown':  process.env.PUBLIC_URL + '/brands/backgrounds/SMACKDOWN_BG.png',
  'NXT':        process.env.PUBLIC_URL + '/brands/backgrounds/NXT_BG.png',
  'NXT 2.0':    process.env.PUBLIC_URL + '/brands/backgrounds/NXT20_BG.png',
  'WCW':        process.env.PUBLIC_URL + '/brands/backgrounds/WCW_BG.png',
  'ECW':        process.env.PUBLIC_URL + '/brands/backgrounds/ECW_BG.png',
  'NXT Mutiny': process.env.PUBLIC_URL + '/brands/backgrounds/NXTMUTINY_BG.png',
};

const BRAND_COLORS_MAP = {
  'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#a8a9ad',
  'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242',
};

export const isBrandParticipant = (p) => !!(p && p.isBrand);

export const makeBrandParticipant = (brandName) => ({
  isBrand:     true,
  brand_id:    brandName,
  wrestler_id: '__brand__' + brandName,
  name:        brandName,
});

const placeholder = process.env.PUBLIC_URL + '/wrestlers/placeholder.png';

const RIVALRY_LEVEL_COLORS = { 1: '#3498db', 2: '#e67e22', 3: '#e74c3c', 4: '#d4af37' };
const PROMO_ACTION_NAMES = {
  'self-promo': 'SELF PROMO', 'call-out': 'CALL OUT', 'tag-team-call-out': 'TAG CALL OUT',
  'role-change': 'ROLE CHANGE', 'advertising': 'ADVERTISING', 'charity': 'CHARITY',
  'class-change': 'CLASS CHANGE', 'training': 'TRAINING', 'invasion': 'INVASION',
};

const PLE_SLOT_LABELS = {
  opener: 'OPENER', mid: 'MID CARD', main_event: 'MAIN EVENT', promo: 'PROMO',
};

const RULE_TEAMS = {
  '1v1': [1,1], 'tag': [2,2], 'triple-threat': [1,1,1], 'fatal-4-way': [1,1,1,1],
  'five-way': [1,1,1,1,1], 'six-way': [1,1,1,1,1,1], '3v3': [3,3],
  'tag-triple-threat': [2,2,2], 'eight-way': [1,1,1,1,1,1,1,1], '4v4': [4,4],
  'tag-fatal-4-way': [2,2,2,2], '1v2': [1,2], '1v3': [1,3], '2v3': [2,3],
};

const groupIntoTeams = (participants, ruleId) => {
  const teamSizes = RULE_TEAMS[ruleId] || [1, 1];
  const teams = [];
  let idx = 0;
  for (const size of teamSizes) {
    teams.push(participants.slice(idx, idx + size));
    idx += size;
  }
  return teams;
};

const css = `
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  .ple-slot-row { transition: background 0.12s, border-color 0.12s; cursor: pointer; }
  .ple-slot-row:hover { filter: brightness(1.12); }
  .ple-slot-row.selected { outline: 2px solid rgba(255,255,255,0.65) !important; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.2); border-radius: 2px; }
  select option { background: #1a1a1a; color: #fff; }
`;

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono:  { fontFamily: "'Monda', sans-serif" },
  panel: { background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '8px' },
};

// ── Helpers ───────────────────────────────────────────────────────────

const getWrestlerRender = (w) => {
  if (!w || isBrandParticipant(w)) return null;
  if (w.customRender) return w.customRender;
  if (w.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png';
  return null;
};

const detectAllRivalries = (participants, rivalries) => {
  if (!rivalries || !participants || participants.length === 0) return [];
  var pIds = participants.filter(Boolean).filter(p => !isBrandParticipant(p)).map(p => String(p.wrestler_id));
  return rivalries.filter(function(rv) {
    if (rv.ended) return false;
    var s1 = (rv.side1 || []).map(String);
    var s2 = (rv.side2 || []).map(String);
    if (!s1.length || !s2.length) return false;
    return s1.every(id => pIds.includes(id)) && s2.every(id => pIds.includes(id));
  });
};

// ── Brand Chip (inline in slot row) ──────────────────────────────────
const BrandChip = ({ brandName, color: colorProp, logo: logoProp }) => {
  var color = colorProp || BRAND_COLORS_MAP[brandName] || '#888';
  var logo  = logoProp  || BRAND_LOGOS[brandName];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: color + '18', border: '1px solid ' + color + '55', borderRadius: '3px', padding: '2px 7px', flexShrink: 0 }}>
      {logo
        ? <img loading="lazy" src={logo} alt={brandName} style={{ height: '11px', objectFit: 'contain' }} onError={function(e) { e.target.style.display='none'; }} />
        : <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.38rem', color: color, letterSpacing: '1px' }}>{brandName.toUpperCase()}</span>
      }
    </div>
  );
};

// ── Brand Card (same gabarit as UnifiedWrestlerCard match/preview) ────
// Uses vertical background image if available, falls back to color gradient.
// Accepts explicit color/logo/bg for custom brands (overrides static maps).
const BrandCard = ({ brandName, width, height, renderHeight, color: colorProp, logo: logoProp, bg: bgProp }) => {
  var color  = colorProp || BRAND_COLORS_MAP[brandName] || '#888';
  var logo   = logoProp  || BRAND_LOGOS[brandName];
  var bgImg  = bgProp    || BRAND_BG_IMAGES[brandName];
  var w  = width       || 143;
  var h  = height      || 330;
  var rh = renderHeight || 264; // image area height (same ratio as wrestler cards)

  return (
    <div style={{ width: w + 'px', height: h + 'px', borderRadius: '8px', overflow: 'hidden', border: '1px solid ' + color + '66', background: 'rgba(0,0,0,0.5)', flexShrink: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Image area */}
      <div style={{ height: rh + 'px', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        {/* Background: vertical brand image or color fallback */}
        {bgImg
          ? <img loading="lazy" src={bgImg} alt=""
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
              onError={function(e) { e.target.style.display='none'; }} />
          : <div style={{ position: 'absolute', inset: 0, background: color + '1a' }} />
        }
        {/* Color overlay for depth */}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center bottom, ' + color + '44 0%, rgba(0,0,0,0.55) 70%)' }} />
        {/* Brand logo centered */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {logo
            ? <img loading="lazy" src={logo} alt={brandName}
                style={{ height: Math.round(w * 0.38) + 'px', objectFit: 'contain', filter: 'drop-shadow(0 0 12px ' + color + 'bb)', maxWidth: '85%' }}
                onError={function(e) { e.target.style.display='none'; }} />
            : <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.1rem', color: color, letterSpacing: '2px', textShadow: '0 0 16px ' + color + '88' }}>{brandName.toUpperCase()}</span>
          }
          <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: color + 'bb', letterSpacing: '2px', textTransform: 'uppercase' }}>BRAND</span>
        </div>
      </div>
      {/* Name bar — matches wrestler card footer */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4px 6px', background: 'rgba(0,0,0,0.92)', borderTop: '1px solid ' + color + '44' }}>
        <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.52rem', color: color, letterSpacing: '1px', textAlign: 'center', lineHeight: 1.2 }}>{brandName.toUpperCase()}</span>
      </div>
    </div>
  );
};

// ── PLE Slot Row (left list) ─────────────────────────────────────────
// pleAccent prop: tint all slots with PLE theme color
const PLESlotRow = ({ slot, isSelected, onClick, onDoubleClick, pleAccent }) => {
  var accent    = pleAccent || '#d4af37';
  var hasBooking = slot.booking && slot.booking.participants && slot.booking.participants.length > 0;
  var isPromo   = slot.type === 'promo';
  var slotLabel = PLE_SLOT_LABELS[slot.type] || slot.type.toUpperCase();

  // Border/highlight color: selected → white glow, booked → accent tinted, empty → subtle
  var borderColor = isSelected
    ? 'rgba(255,255,255,0.55)'
    : hasBooking
      ? accent + '55'
      : 'rgba(255,255,255,0.08)';
  var bgColor = isSelected ? accent + '12' : 'rgba(8,8,8,0.8)';

  return (
    <div className={'ple-slot-row' + (isSelected ? ' selected' : '')} onClick={onClick} onDoubleClick={onDoubleClick}
      style={{ borderRadius: '6px', overflow: 'hidden', border: '1px solid ' + borderColor, background: bgColor, display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>

      {/* Top label bar */}
      <div style={{ padding: '4px 8px', background: isSelected ? accent + '22' : 'rgba(0,0,0,0.45)', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1.5px', color: accent }}>{slotLabel}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {slot.isInterBrand && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: accent + 'aa', letterSpacing: '0.5px', border: '1px solid ' + accent + '33', borderRadius: '2px', padding: '0px 4px' }}>INTER-BRAND</span>}
          {hasBooking && !isPromo && slot.booking.cost > 0 && (
            <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: accent }}>${(slot.booking.cost || 0).toLocaleString('fr-FR')}</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '6px 8px', minHeight: '56px', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {hasBooking ? (
          isPromo ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, overflow: 'hidden' }}>
              <img loading="lazy" src={getWrestlerRender(slot.booking.participants[0]) || placeholder} alt=""
                style={{ height: '52px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.8))', flexShrink: 0 }}
                onError={function(e) { e.target.src = placeholder; }} />
              <div>
                {slot.booking.promoAction && (
                  <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.5rem', letterSpacing: '1px', color: accent }}>{PROMO_ACTION_NAMES[slot.booking.promoAction] || slot.booking.promoAction}</div>
                )}
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {slot.booking.participants[0] ? getWrestlerNameUpper(slot.booking.participants[0], 'display') : ''}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1, flexWrap: 'wrap', overflow: 'hidden' }}>
              {(function() {
                var rule  = slot.booking.matchRule || '1v1';
                var sizes = RULE_TEAMS[rule] || [1, 1];
                var allP  = slot.booking.participants || [];
                var teams = [];
                var i = 0;
                sizes.forEach(function(size) { teams.push(allP.slice(i, i + size)); i += size; });
                return teams.map(function(team, ti) {
                  return (
                    <React.Fragment key={ti}>
                      {ti > 0 && <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.5rem', color: '#e74c3c', flexShrink: 0 }}>VS</span>}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                        {team.filter(Boolean).map(function(p, pi) {
                          return (
                            <React.Fragment key={pi}>
                              {pi > 0 && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)' }}>&amp;</span>}
                              {isBrandParticipant(p)
                                ? <BrandChip brandName={p.brand_id} color={p.brandColor} logo={p.brandLogo} />
                                : (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <img loading="lazy" src={getWrestlerRender(p) || placeholder} alt=""
                                      style={{ height: '44px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }}
                                      onError={function(e) { e.target.src = placeholder; }} />
                                    <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getWrestlerNameUpper(p, 'display')}</span>
                                  </div>
                                )
                              }
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
              <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px', flexShrink: 0 }}>
                {slot.booking.matchRule && slot.booking.matchRule !== '1v1' && (
                  <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: accent + 'aa', background: accent + '10', padding: '1px 5px', borderRadius: '2px', whiteSpace: 'nowrap', border: '1px solid ' + accent + '25' }}>
                    {(MATCH_RULES.find(function(r) { return r.id === slot.booking.matchRule; }) || {}).name || slot.booking.matchRule}
                  </span>
                )}
                {slot.booking.matchType && slot.booking.matchType !== 'normal' && (
                  <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.04)', padding: '1px 5px', borderRadius: '2px', whiteSpace: 'nowrap' }}>
                    {(MATCH_TYPES.find(function(m) { return m.id === slot.booking.matchType; }) || {}).name || slot.booking.matchType}
                  </span>
                )}
              </div>
            </div>
          )
        ) : (
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.56rem', color: accent + '55', letterSpacing: '1px' }}>
            {isPromo ? '+ BOOK PROMO' : '+ BOOK MATCH'}
          </div>
        )}
      </div>
    </div>
  );
};

// ── PLE Slot Preview (right panel) ───────────────────────────────────
const PLESlotPreview = ({ slot, brandColor, brandBg, pleAccentColor, onEdit, onClear, champions, roster, rivalries }) => {
  const pleScrollRef = React.useRef(null);
  const [showPleArrows, setShowPleArrows] = React.useState(false);

  React.useEffect(function() {
    var el = pleScrollRef.current;
    if (!el) return;
    var check = function() { setShowPleArrows(el.scrollWidth > el.clientWidth + 4); };
    check();
    var ro = new ResizeObserver(check);
    ro.observe(el);
    return function() { ro.disconnect(); };
  }, [slot]);

  const scrollPle = function(dir) {
    if (pleScrollRef.current) pleScrollRef.current.scrollBy({ left: dir * 160, behavior: 'smooth' });
  };

  if (!slot) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', ...S.mono, fontSize: '0.6rem', color: 'rgba(255,255,255,0.15)', letterSpacing: '2px' }}>
        SELECT A SLOT
      </div>
    );
  }

  var hasBooking  = slot.booking && slot.booking.participants && slot.booking.participants.length > 0;
  var isPromo     = slot.type === 'promo';
  var usedBg      = brandBg;
  var usedColor   = pleAccentColor || brandColor || '#d4af37';
  var p           = (slot.booking && slot.booking.participants) || [];
  var allRvs      = (!isPromo && p.length > 0) ? detectAllRivalries(p, rivalries || []) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
      {/* Title row */}
      <div style={{ padding: '0 0 12px', display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', flexShrink: 0 }}>
        <div style={{ ...S.anton, fontSize: '1.8rem', letterSpacing: '3px', color: usedColor }}>{PLE_SLOT_LABELS[slot.type] || slot.type.toUpperCase()}</div>
        {slot.isInterBrand && <div style={{ ...S.mono, fontSize: '0.48rem', color: usedColor + 'aa', letterSpacing: '1px', border: '1px solid ' + usedColor + '33', borderRadius: '3px', padding: '1px 8px' }}>INTER-BRAND</div>}
        {!isPromo && slot.booking && slot.booking.matchType && slot.booking.matchType !== 'normal' && (
          <div style={{ ...S.mono, fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px' }}>
            {(MATCH_TYPES.find(function(m) { return m.id === slot.booking.matchType; }) || {}).name || ''}
          </div>
        )}
        {isPromo && slot.booking && slot.booking.promoAction && (
          <div style={{ ...S.mono, fontSize: '0.7rem', color: usedColor, letterSpacing: '1px' }}>
            {(PROMO_ACTIONS.find(function(a) { return a.id === slot.booking.promoAction; }) || {}).name || ''}
          </div>
        )}
        {allRvs.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {allRvs.map(function(rv) {
              var lvlColor = RIVALRY_LEVEL_COLORS[rv.level] || '#e74c3c';
              var getSideName = function(ids) {
                return (ids || []).map(function(id) {
                  var w = (roster || []).find(function(r) { return String(r.wrestler_id) === String(id); });
                  return w ? getWrestlerName(w, 'display') : '?';
                }).filter(Boolean).join(' & ');
              };
              return (
                <div key={rv.id} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '3px', background: lvlColor + '18', border: '1px solid ' + lvlColor + '55' }}>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    {[1,2,3,4].map(function(l) { return <div key={l} style={{ width: '8px', height: '3px', borderRadius: '1px', background: l <= rv.level ? lvlColor : 'rgba(255,255,255,0.1)' }} />; })}
                  </div>
                  <span style={{ ...S.mono, fontSize: '0.4rem', color: lvlColor, letterSpacing: '0.5px' }}>
                    {rv.teamName1 || getSideName(rv.side1)} <span style={{ opacity: 0.6 }}>vs</span> {rv.teamName2 || getSideName(rv.side2)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Participants area */}
      {hasBooking && !isPromo ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', minHeight: 0, alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
          {usedBg && <img loading="lazy" src={usedBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(8px) brightness(0.25)', zIndex: 0 }} />}
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, ' + usedColor + '18 0%, transparent 70%)', zIndex: 1 }} />
          {(function() {
            var teams  = groupIntoTeams(p, slot.booking.matchRule || '1v1');
            var cardH  = 330;
            return (
              <div style={{ position: 'relative', zIndex: 2, width: '100%' }}>
                {/* Left arrow */}
                {showPleArrows && (
                  <button onClick={function() { scrollPle(-1); }} style={{ position: 'absolute', left: '4px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.65)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', flexShrink: 0 }}>‹</button>
                )}
                <div ref={pleScrollRef} style={{ display: 'flex', alignItems: 'center', justifyContent: showPleArrows ? 'flex-start' : 'center', padding: '16px', gap: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  <style>{'.ple-scroll::-webkit-scrollbar{display:none}'}</style>
                  <div className="ple-scroll" style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                    {teams.map(function(team, ti) {
                      return (
                        <React.Fragment key={ti}>
                          {ti > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 12px', flexShrink: 0 }}>
                              <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '2rem', color: '#e74c3c', textShadow: '0 0 20px rgba(231,76,60,0.8)' }}>VS</span>
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '1px' }}>
                            {team.map(function(participant, wi) {
                              if (!participant) return null;
                              if (isBrandParticipant(participant)) {
                                return <BrandCard key={wi} brandName={participant.brand_id} width={143} height={cardH} renderHeight={264} color={participant.brandColor} logo={participant.brandLogo} bg={participant.brandBg} />;
                              }
                              var champSlotsP    = (champions && champions.slots) || [];
                              var champMapP      = (champions && champions.champions) || {};
                              var previewIsChamp = champSlotsP.some(function(cs) {
                                var champs = champMapP[cs.key] || [];
                                return champs.some(function(c) { return c && String(c.wrestler_id) === String(participant.wrestler_id); });
                              });
                              var previewHasSynergy  = hasSynergyWithGroup ? hasSynergyWithGroup(participant, p) : false;
                              var previewIsSpecialist = isMatchSpecialist   ? isMatchSpecialist(participant, slot.booking.matchType) : false;
                              return (
                                <UnifiedWrestlerCard
                                  key={wi}
                                  wrestler={participant}
                                  variant="preview"
                                  brandColor={usedColor}
                                  champions={champions}
                                  roster={roster}
                                  rivalries={rivalries}
                                  hasSynergy={previewHasSynergy}
                                  isMatchSpecialist={previewIsSpecialist}
                                  isChampionGlow={previewIsChamp}
                                  isSpecialistGlow={previewIsSpecialist && !previewIsChamp}
                                  matchType={slot.booking.matchType}
                                  size={{ width: 143, height: cardH, renderHeight: 264 }}
                                />
                              );
                            })}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
                {/* Right arrow */}
                {showPleArrows && (
                  <button onClick={function() { scrollPle(1); }} style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.65)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', flexShrink: 0 }}>›</button>
                )}
              </div>
            );
          })()}
        </div>
      ) : hasBooking && isPromo ? (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
          {usedBg && <img loading="lazy" src={usedBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(8px) brightness(0.25)', zIndex: 0 }} />}
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, ' + usedColor + '14 0%, transparent 70%)', zIndex: 1 }} />
          {(function() {
            var promoData    = slot.booking.promoAction ? PROMO_ACTIONS.find(function(a) { return a.id === slot.booking.promoAction; }) : null;
            var promoTargets = slot.booking.promoTargets || [];
            var showArrow    = promoTargets.length > 0;
            var renderStar   = function(w, i) {
              if (!w) return null;
              var champSlotsPromo = (champions && champions.slots) || [];
              var champMapPromo   = (champions && champions.champions) || {};
              var promoIsChamp = champSlotsPromo.some(function(cs) {
                return (champMapPromo[cs.key] || []).some(function(c) { return c && String(c.wrestler_id) === String(w.wrestler_id); });
              });
              return (
                <UnifiedWrestlerCard
                  key={i}
                  wrestler={w}
                  variant="preview"
                  brandColor={usedColor}
                  champions={champions}
                  roster={roster}
                  rivalries={rivalries}
                  isChampionGlow={promoIsChamp}
                  size={{ width: 143, height: 330, renderHeight: 264 }}
                />
              );
            };
            return (
              <div style={{ position: 'relative', zIndex: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {promoData && (
                  <div style={{ padding: '8px 16px', textAlign: 'center', borderBottom: '1px solid ' + usedColor + '33' }}>
                    <div style={{ ...S.mono, fontSize: '0.62rem', color: usedColor, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '2px' }}>{promoData.name}</div>
                    <div style={{ ...S.mono, fontSize: '0.44rem', color: 'rgba(255,255,255,0.35)', lineHeight: '1.4' }}>{promoData.description}</div>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '16px' }}>
                  <div style={{ display: 'flex', gap: '1px' }}>{p.map(renderStar)}</div>
                  {showArrow && <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '2rem', color: '#e74c3c', textShadow: '0 0 20px rgba(231,76,60,0.8)', padding: '0 8px', flexShrink: 0 }}>→</span>}
                  {showArrow && <div style={{ display: 'flex', gap: '1px' }}>{promoTargets.map(renderStar)}</div>}
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', ...S.mono, fontSize: '0.58rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '1px' }}>
          Select a slot to book
        </div>
      )}

      {/* Bottom bar */}
      <div style={{ flexShrink: 0, paddingTop: '12px', marginTop: '8px', borderTop: '1px solid ' + usedColor + '22', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {slot.booking && slot.booking.cost > 0 && (
            <>
              <div style={{ ...S.mono, fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>{isPromo ? 'PROMO' : 'MATCH'} COST</div>
              <div style={{ ...S.anton, fontSize: '1rem', color: usedColor, letterSpacing: '1px' }}>${(slot.booking.cost || 0).toLocaleString('fr-FR')}</div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {slot.booking && (
            <button onClick={onClear}
              style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.62rem', letterSpacing: '1.5px', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.4)', color: 'rgba(231,76,60,0.7)', padding: '10px 18px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}
              onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(231,76,60,0.18)'; e.currentTarget.style.color = '#e74c3c'; }}
              onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(231,76,60,0.08)'; e.currentTarget.style.color = 'rgba(231,76,60,0.7)'; }}>
              ✕ CLEAR
            </button>
          )}
          <button onClick={onEdit}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.62rem', letterSpacing: '1.5px', background: slot.booking ? usedColor + '1a' : 'rgba(255,255,255,0.05)', border: slot.booking ? '1px solid ' + usedColor + '99' : '1px solid rgba(255,255,255,0.35)', color: slot.booking ? usedColor : '#fff', padding: '10px 24px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
            {slot.booking ? (isPromo ? '✏ EDIT PROMO' : '✏ EDIT MATCH') : (isPromo ? '+ BOOK PROMO' : '+ BOOK MATCH')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────
export default function PLEBookingPage({ gameData, brand, brandBg, onUpdateGame, onRunShow }) {
  const brandColor    = ({ 'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#a8a9ad', 'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242' })[brand] || '#d4af37';
  const week          = (gameData && gameData.week) || 1;
  const roster        = (gameData && gameData.roster) || [];
  const rivals        = ((gameData && gameData.rivals) || []).filter(Boolean);
  const championsData = (gameData && gameData.champions) || {};
  const difficulty    = (gameData && gameData.difficulty) || 'normal';
  const rivalries     = (gameData && gameData.rivalries) || [];
  const logistics     = (gameData && gameData.logistics) || {};
  const tagTeams      = (gameData && gameData.tagTeams) || [];

  const pleId     = gameData && gameData.nextPle;
  const pleData   = pleId ? PLES.find(function(p) { return p.id === pleId; }) : null;
  const pleTheme  = pleId ? getPLETheme(pleId) : null;
  const pleAccent = (pleTheme && pleTheme.color) || '#d4af37';
  const pleBg     = pleId ? getPLEBg(pleId) : brandBg;

  // Build rivalBrands for the picker — includes custom brand assets if present
  var rivalBrands = rivals.map(function(r) {
    return {
      brand:      r.brand,
      color:      r.brandColor || BRAND_COLORS_MAP[r.brand] || '#888',
      logo:       r.brandLogo  || BRAND_LOGOS[r.brand]      || null,
      bg:         r.brandBg    || BRAND_BG_IMAGES[r.brand]   || null,
      isCustom:   !!r.brandIsCustom,
    };
  }).filter(function(rb) { return !!rb.brand; });

  // ── Slot ID counter (useRef) — avoids duplicate IDs on add/remove ────
  // We use Date.now() + index so IDs are always unique regardless of history
  const slotCounterRef = useRef(100);
  var nextSlotId = function(prefix) {
    slotCounterRef.current += 1;
    return prefix + '-' + slotCounterRef.current;
  };

  // ── Default slot structure ───────────────────────────────────────────
  const buildDefaultSlots = function() {
    return [
      { id: 'ple-opener',  type: 'opener',     booking: null, isInterBrand: true },
      { id: 'ple-mid-0',   type: 'mid',        booking: null, isInterBrand: false },
      { id: 'ple-promo-0', type: 'promo',      booking: null, isInterBrand: false },
      { id: 'ple-main',    type: 'main_event', booking: null, isInterBrand: true },
    ];
  };

  const [slots, setSlots] = useState(function() {
    var saved = gameData && gameData.pleSlotsV2;
    if (!saved || !Array.isArray(saved) || saved.length < 4) return buildDefaultSlots();
    return saved;
  });

  // Reset slots when the PLE or week changes (new PLE = fresh card)
  const prevPleRef = React.useRef(pleId);
  const prevWeekRef = React.useRef(gameData && gameData.week);
  React.useEffect(function() {
    var currentPle  = pleId;
    var currentWeek = gameData && gameData.week;
    if (currentPle !== prevPleRef.current || currentWeek !== prevWeekRef.current) {
      prevPleRef.current  = currentPle;
      prevWeekRef.current = currentWeek;
      var saved = gameData && gameData.pleSlotsV2;
      if (!saved || !Array.isArray(saved) || saved.length < 4) {
        setSlots(buildDefaultSlots());
        setSelectedSlotId('ple-opener');
      } else {
        setSlots(saved);
      }
    }
  }, [pleId, gameData && gameData.week]);

  const [selectedSlotId, setSelectedSlotId] = useState(slots[0] && slots[0].id);
  const [matchMakerSlotId, setMatchMakerSlotId] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);

  var matchMakerSlot = slots.find(function(s) { return s.id === matchMakerSlotId; });
  var selectedSlot   = slots.find(function(s) { return s.id === selectedSlotId; });

  // ── Persist ──────────────────────────────────────────────────────────
  var saveSlots = function(updated) {
    setSlots(updated);
    var canRun = checkCanRun(updated);
    if (onUpdateGame) onUpdateGame({ ...gameData, pleSlotsV2: updated, bookingSlots: updated, bookingFinished: canRun });
  };

  // ── checkCanRun: opener + main + ALL mid cards must be booked ────────
  var checkCanRun = function(sl) {
    var opener = sl.find(function(s) { return s.type === 'opener'; });
    var main   = sl.find(function(s) { return s.type === 'main_event'; });
    var mids   = sl.filter(function(s) { return s.type === 'mid'; });
    var opOk   = opener && opener.booking && opener.booking.participants && opener.booking.participants.length > 0;
    var mainOk = main   && main.booking   && main.booking.participants   && main.booking.participants.length > 0;
    // ALL mid card slots must be booked (not just one)
    var midOk  = mids.length > 0 && mids.every(function(s) {
      return s.booking && s.booking.participants && s.booking.participants.length > 0;
    });
    return !!(opOk && mainOk && midOk);
  };

  var canRunShow   = checkCanRun(slots);
  var midPairCount = slots.filter(function(s) { return s.type === 'mid'; }).length;

  // ── Mid card management ──────────────────────────────────────────────
  var addMidCardPair = function() {
    if (midPairCount >= 4) return;
    // Use unique IDs based on timestamp to prevent React key collisions
    var uid      = Date.now();
    var newMid   = { id: 'ple-mid-' + uid,       type: 'mid',   booking: null, isInterBrand: false };
    var newPromo = { id: 'ple-promo-' + (uid + 1), type: 'promo', booking: null, isInterBrand: false };
    var updated  = [...slots.slice(0, slots.length - 1), newMid, newPromo, slots[slots.length - 1]];
    saveSlots(updated);
  };

  var removeMidCardPair = function(midId) {
    var midIdx = slots.findIndex(function(s) { return s.id === midId; });
    if (midIdx < 0) return;
    var nextSlot = slots[midIdx + 1];
    var toRemove = [midIdx];
    if (nextSlot && nextSlot.type === 'promo') toRemove.push(midIdx + 1);
    var updated = slots.filter(function(_, i) { return !toRemove.includes(i); });
    saveSlots(updated);
    if (selectedSlotId === midId || (nextSlot && selectedSlotId === nextSlot.id)) {
      setSelectedSlotId(updated[0] && updated[0].id);
    }
  };

  // ── Blocked IDs ──────────────────────────────────────────────────────
  var getBlockedIds = function(slotId, isCurrentPromo) {
    return slots
      .filter(function(s) {
        return s.id !== slotId &&
               (isCurrentPromo ? s.type === 'promo' : s.type !== 'promo') &&
               s.booking && s.booking.participants;
      })
      .flatMap(function(s) {
        return s.booking.participants.filter(Boolean)
          .filter(function(p) { return !isBrandParticipant(p); })
          .map(function(p) { return p.wrestler_id; });
      });
  };

  var allMatchBookedIds = slots
    .filter(function(s) { return s.type !== 'promo' && s.booking && s.booking.participants; })
    .flatMap(function(s) {
      return s.booking.participants.filter(Boolean)
        .filter(function(p) { return !isBrandParticipant(p); })
        .map(function(p) { return p.wrestler_id; });
    });

  // ── Costs ────────────────────────────────────────────────────────────
  var totalCost = slots.reduce(function(sum, s) { return sum + ((s.booking && s.booking.cost) || 0); }, 0);
  var budget    = (gameData && gameData.budget) || 3000000;

  // ── Suggested matches ────────────────────────────────────────────────
  var hotRivalries   = rivalries.filter(function(r) { return r && r.level >= 3 && !r.ended; });
  var matchBookedSet = new Set(allMatchBookedIds.map(String));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '12px' }}>
      <style>{css}</style>
      <style>{WrestlerCardStyles}</style>

      {/* MatchMaker overlay — same for all slot types */}
      {matchMakerSlot && (
        <MatchMaker
          slot={matchMakerSlot}
          roster={roster}
          champions={championsData}
          gmName={(gameData && gameData.gm) || ''}
          rivals={rivals}
          rivalries={rivalries}
          tagTeams={tagTeams}
          difficulty={difficulty}
          logistics={logistics}
          brandColor={pleAccent}
          brandBg={pleBg}
          matchTypeSetting={(gameData && gameData.gameSettingsData && gameData.gameSettingsData.matchType) || 'classic'}
          matchBookedIds={getBlockedIds(matchMakerSlot.id, matchMakerSlot.type === 'promo')}
          rivalBrands={matchMakerSlot.isInterBrand ? rivalBrands : null}
          disableTitleMatch={matchMakerSlot.isInterBrand}
          onClose={function() { setMatchMakerSlotId(null); }}
          onSave={function(booking) {
            var updated = slots.map(function(s) { return s.id === matchMakerSlotId ? { ...s, booking } : s; });
            saveSlots(updated);
            setMatchMakerSlotId(null);
          }}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
        {pleData && (
          <img loading="lazy" src={getPLELogo(pleData.id)} alt=""
            style={{ height: '38px', objectFit: 'contain', filter: 'drop-shadow(0 0 12px ' + pleAccent + 'aa)' }}
            onError={function(e) { e.target.style.display = 'none'; }} />
        )}
        <div>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1rem', letterSpacing: '2px', color: pleAccent }}>
            {pleData ? pleData.name.toUpperCase() : 'PLE'}
          </div>
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>
            WEEK {week} — PREMIUM LIVE EVENT
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>SHOW COST</div>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', color: totalCost > budget ? '#e74c3c' : pleAccent }}>${totalCost.toLocaleString('fr-FR')}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>BUDGET</div>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', color: '#2ecc71' }}>${budget.toLocaleString('fr-FR')}</div>
          </div>
          {saveStatus && (
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', color: saveStatus === 'saved' ? '#2ecc71' : 'rgba(255,255,255,0.4)', letterSpacing: '0.5px' }}>
              {saveStatus === 'saving' ? '⏳ Saving...' : '✓ Saved!'}
            </div>
          )}
          <button disabled={!canRunShow} onClick={function() { if (canRunShow && onRunShow) onRunShow(); }}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.6rem', letterSpacing: '1.5px', background: canRunShow ? 'rgba(46,204,113,0.15)' : 'rgba(255,255,255,0.04)', border: '1px solid ' + (canRunShow ? 'rgba(46,204,113,0.6)' : 'rgba(255,255,255,0.1)'), color: canRunShow ? '#2ecc71' : 'rgba(255,255,255,0.2)', padding: '8px 20px', borderRadius: '4px', cursor: canRunShow ? 'pointer' : 'not-allowed', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
            {canRunShow ? '▶ RUN SHOW' : '🔒 INCOMPLETE'}
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '580px 1fr', gap: '14px', minHeight: 0, overflow: 'hidden' }}>

        {/* LEFT — Slot list */}
        <div style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid ' + pleAccent + '33', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' }}>

          {/* Suggested matches */}
          {hotRivalries.length > 0 && (
            <div style={{ padding: '8px 12px', background: pleAccent + '0a', border: '1px solid ' + pleAccent + '33', borderRadius: '6px', marginBottom: '4px' }}>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: pleAccent, letterSpacing: '1px', marginBottom: '5px' }}>⭐ SUGGESTED MATCHES</div>
              {hotRivalries.map(function(r, i) {
                var s1 = (r.side1 || []).map(function(id) { return roster.find(function(w) { return w.wrestler_id === id || w.wrestler_id === Number(id); }); }).filter(Boolean);
                var s2 = (r.side2 || []).map(function(id) { return roster.find(function(w) { return w.wrestler_id === id || w.wrestler_id === Number(id); }); }).filter(Boolean);
                var lvlColor = { 3: '#e74c3c', 4: '#9b59b6' }[r.level] || '#e74c3c';
                var lvlLabel = { 3: 'Niv.3', 4: 'Niv.4 🔥' }[r.level] || '';
                var allIds   = s1.concat(s2).map(function(w) { return String(w.wrestler_id); });
                var isBooked = allIds.length > 0 && allIds.every(function(id) { return matchBookedSet.has(id); });
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < hotRivalries.length - 1 ? '4px' : 0, opacity: isBooked ? 0.5 : 1 }}>
                    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: isBooked ? '#2ecc71' : lvlColor, background: (isBooked ? '#2ecc71' : lvlColor) + '18', border: '1px solid ' + (isBooked ? '#2ecc71' : lvlColor) + '44', borderRadius: '3px', padding: '1px 5px', flexShrink: 0 }}>
                      {isBooked ? '✓ BOOKED' : lvlLabel}
                    </div>
                    <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.52rem', color: isBooked ? 'rgba(255,255,255,0.4)' : '#fff', letterSpacing: '0.5px', textDecoration: isBooked ? 'line-through' : 'none' }}>
                      {s1.map(function(w) { return getWrestlerName(w, 'short'); }).join(' & ')}
                      <span style={{ color: isBooked ? 'rgba(255,255,255,0.25)' : '#e74c3c', margin: '0 5px' }}>VS</span>
                      {s2.map(function(w) { return getWrestlerName(w, 'short'); }).join(' & ')}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: pleAccent + '88', letterSpacing: '1.5px', marginBottom: '2px' }}>CARD</div>

          {/* Slot rows */}
          {slots.map(function(slot) {
            var isMid  = slot.type === 'mid';
            var isLast = slot.type === 'main_event';
            return (
              <div key={slot.id} style={{ position: 'relative' }}>
                {/* "+ ADD MID CARD SLOT" button above main event */}
                {isLast && midPairCount < 4 && (
                  <button onClick={addMidCardPair}
                    style={{ display: 'block', width: '100%', marginBottom: '5px', padding: '7px', fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', letterSpacing: '1px', background: 'transparent', border: '1px dashed ' + pleAccent + '33', color: pleAccent + '66', borderRadius: '5px', cursor: 'pointer', transition: 'border-color 0.15s, color 0.15s, background 0.15s' }}
                    onMouseEnter={function(e) { e.currentTarget.style.borderColor = pleAccent + '77'; e.currentTarget.style.color = pleAccent; e.currentTarget.style.background = pleAccent + '0a'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.borderColor = pleAccent + '33'; e.currentTarget.style.color = pleAccent + '66'; e.currentTarget.style.background = 'transparent'; }}>
                    + ADD MID CARD SLOT
                  </button>
                )}
                <PLESlotRow
                  slot={slot}
                  isSelected={selectedSlotId === slot.id}
                  pleAccent={pleAccent}
                  onClick={function() { setSelectedSlotId(slot.id); }}
                  onDoubleClick={function() { setMatchMakerSlotId(slot.id); setSelectedSlotId(slot.id); }}
                />
                {/* "✕ REMOVE" on non-first mid card slots */}
                {isMid && midPairCount > 1 && (
                  <button onClick={function(e) { e.stopPropagation(); removeMidCardPair(slot.id); }}
                    style={{ position: 'absolute', top: '5px', right: '8px', background: 'rgba(231,76,60,0.12)', border: '1px solid rgba(231,76,60,0.35)', color: '#e74c3c', borderRadius: '3px', padding: '1px 7px', fontSize: '0.38rem', fontFamily: "'Monda',sans-serif", cursor: 'pointer', zIndex: 5, letterSpacing: '0.5px' }}>
                    ✕ REMOVE
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT — Preview */}
        <div style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid ' + pleAccent + '33', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
          {selectedSlot && selectedSlot.isInterBrand ? (
            <PLESlotPreview
              slot={selectedSlot}
              brandColor={brandColor}
              brandBg={pleBg}
              pleAccentColor={pleAccent}
              champions={championsData}
              roster={roster}
              rivalries={rivalries}
              onEdit={function() { if (selectedSlot) setMatchMakerSlotId(selectedSlot.id); }}
              onClear={function() {
                if (!selectedSlot) return;
                var updated = slots.map(function(s) { return s.id === selectedSlotId ? { ...s, booking: null } : s; });
                saveSlots(updated);
              }}
            />
          ) : (
            <SlotPreview
              slot={selectedSlot}
              brandColor={pleAccent}
              brandBg={pleBg}
              champions={championsData}
              roster={roster}
              rivalries={rivalries}
              onEdit={function() { if (selectedSlot) setMatchMakerSlotId(selectedSlot.id); }}
              onClear={function() {
                if (!selectedSlot) return;
                var updated = slots.map(function(s) { return s.id === selectedSlotId ? { ...s, booking: null } : s; });
                saveSlots(updated);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
